import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Logo } from "@/components/Logo";
import { COPYRIGHT_LINE } from "@/components/Footer";
import { BetaEnrollDialog } from "@/components/BetaEnrollDialog";
import { useForceLight } from "@/context/ThemeContext";
import { TermsDialog } from "@/components/TermsDialog";
import { Button } from "@/components/ui/button";

const FEATURES = [
  ["TestTubes", "Sample & Test Records", "CBC, ESR, coagulation, marrow, SPEP, HBEP, flow cytometry and more — every panel, one register."],
  ["Timer", "Automatic TAT", "Turnaround time and breach status computed from your collection, receipt and report timestamps."],
  ["Siren", "Critical Alerts", "Log critical values with notification, read-back confirmation and time-to-notify."],
  ["ShieldCheck", "QC with Westgard", "1-2s, 1-3s, 2-2s, R-4s, 4-1s and 10x applied automatically, with Levey-Jennings trends."],
  ["Boxes", "Inventory & Indents", "Reorder levels, lot expiry, one-click indents and a weekly digest in your inbox."],
  ["BookOpen", "SOP & Equipment", "Controlled SOP versions, maintenance logs, calibration due dates and EQAS performance."],
];

export default function Landing() {
  useForceLight();
  const [betaOpen, setBetaOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFDFD]" data-testid="landing-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setBetaOpen(true)} data-testid="header-beta-btn" className="hidden sm:inline-flex">
              Enrol as beta user
            </Button>
            <Link to="/login">
              <Button data-testid="header-signin-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">Sign in</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-16 pb-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#002FA7]">Haematology laboratory management</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#111827] mt-4 leading-[1.05]">
            The daily operating system for your laboratory.
          </h1>
          <p className="text-base text-[#6B7280] mt-6 max-w-xl">
            LabTrack<sup className="text-[10px]">TM</sup> keeps samples, turnaround time, critical alerts,
            rejections, repeat runs, inventory, indents, quality control, SOPs, equipment logs and proficiency
            testing in one place — every dropdown customisable, every module exportable to Excel, every record
            printable and fully audited.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={() => setBetaOpen(true)} data-testid="hero-beta-btn"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white h-11 px-6">
              Enrol as a beta user
            </Button>
            <a href="mailto:info@pathlete.org?subject=LabTrack%20access%20request">
              <Button variant="outline" data-testid="hero-contact-btn" className="h-11 px-6">
                Request lab access
              </Button>
            </a>
          </div>
        </div>

        <div className="rounded-md border border-[#E5E7EB] bg-white p-6" data-testid="access-instructions">
          <div className="flex items-center gap-2">
            <Icons.Lock className="h-4 w-4 text-[#002FA7]" />
            <h2 className="font-heading font-bold text-[#111827]">Access is granted by invitation only</h2>
          </div>
          <p className="text-sm text-[#6B7280] mt-3">
            Accounts cannot be created by self sign-up. Laboratories are onboarded directly by the developer.
          </p>
          <ol className="mt-4 space-y-3 text-sm text-[#111827]">
            {[
              <>Email <a href="mailto:info@pathlete.org" className="text-[#002FA7] hover:underline font-medium" data-testid="contact-email-link">info@pathlete.org</a> from your official address.</>,
              <>Include your <b>laboratory name</b>, city, and the name and designation of the authorised representative.</>,
              <>State the <b>number of users</b> needed and their roles (pathologist / technician / administrator).</>,
              <>Our team verifies the request and provisions your accounts with temporary passwords.</>,
              <>Sign in, change your password, and enable two-factor authentication for administrators.</>,
            ].map((line, i) => (
              <li key={i} className="flex gap-3">
                <span className="h-5 w-5 shrink-0 rounded-full bg-[#002FA7] text-white text-[11px] flex items-center justify-center">{i + 1}</span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
          <div className="mt-5 rounded-md bg-[#F3F4F6] p-3">
            <p className="text-xs text-[#6B7280]">
              Beta enrolment is open to qualified laboratory professionals. Apply using the button above —
              approval is at the sole discretion of PathLete India Pvt. Ltd.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-12 border-t border-[#E5E7EB]">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#111827]">Everything the bench needs</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {FEATURES.map(([icon, title, desc]) => {
            const Icon = Icons[icon] || Icons.Circle;
            return (
              <div key={title} className="rounded-md border border-[#E5E7EB] bg-white p-5 hover:border-[#002FA7] transition-colors"
                data-testid={`feature-${title.replace(/\s+/g, "-").toLowerCase()}`}>
                <Icon className="h-5 w-5 text-[#002FA7]" />
                <h3 className="font-heading font-semibold text-[#111827] mt-3">{title}</h3>
                <p className="text-sm text-[#6B7280] mt-1.5">{desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="font-heading font-bold text-[#111827]">
              LabTrack<sup className="text-[9px]">TM</sup> by PathLete<sup className="text-[8px]">®</sup>
            </p>
            <p className="text-xs text-[#6B7280] mt-1">
              PathLete India Pvt. Ltd. · <a href="mailto:info@pathlete.org" className="text-[#002FA7] hover:underline">info@pathlete.org</a>
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => setTermsOpen(true)} data-testid="footer-terms-btn" className="text-[#002FA7] hover:underline">
              Terms &amp; Conditions
            </button>
            <Link to="/login" className="text-[#002FA7] hover:underline" data-testid="footer-signin-link">Sign in</Link>
          </div>
        </div>
        <div className="mt-6 text-center text-xs text-[#6B7280]" data-testid="app-footer">
          <p>Proudly developed in India by PathLete Technologies</p>
          <p className="mt-1" data-testid="app-copyright">{COPYRIGHT_LINE}</p>
        </div>
      </footer>

      <BetaEnrollDialog open={betaOpen} onClose={() => setBetaOpen(false)} />
      <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}
